import { zodResolver } from "@hookform/resolvers/zod";
import { creditProposalPersonalDataSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { PersonalDataFormType } from "@repo/core/stores/proposalForm/types";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { CommonResource } from "@repo/core/services/resources/common";
import { apiInstance } from "@repo/core/services/instances/api";
import { useQuery } from "@tanstack/react-query";
import { formatDateFromISO, mapCreditProposalToStoreData, enrichCreditProposalSnapshotForPersist } from "../hooks/useCreditProposal/mappers";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { GenderEnum, LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { usePreProposalStore } from "@repo/core/screens/PreProposal/store/preProposalStore";
import { isReanalysisProposal } from "@repo/core/screens/PreProposal/screens/BiometryValidation/services/navigation.service";
import {
  limitParentName,
  sanitizeParentNameForApi
} from "@repo/core/utils/parentName.utils";

const commonResource = new CommonResource(apiInstance);

const hasLimitReassessment = (proposal?: {
  strReavaliacaoLimite?: string | null;
}): boolean =>
  proposal?.strReavaliacaoLimite === LimitReassessmentEnum.REANALYSIS;

export const PersonalDataFormController = () => {
  const { back, completeStep, goToPreviousStep } = useProposalFlowNavigation();
  const routeParams = useRouteParams() as {
    proposalId?: string;
  };
  const proposalId = routeParams?.proposalId;
  const preProposalData = usePreProposalStore((state) => state.proposalData);
  const {
    proposalData,
    getProposalData,
    updateProposalData,
    resetCreditProposalStore,
    setReanalysis,
    persistFullProposalSnapshot
  } = useCreditProposal();

  // Check if we already have matching data in the store
  const hasMatchingData =
    proposalData &&
    proposalId &&
    proposalData.lngProposta &&
    String(proposalData.lngProposta) === String(proposalId);

  // Only fetch if we have a proposalId but no matching data in store
  // User controls when to clear store, so when cleared, fetch will happen
  const shouldFetchProposal = !!proposalId && !hasMatchingData;

  const {
    data: queryProposalData,
    isLoading: isLoadingProposal,
    isFetching: isFetchingProposal
  } = useQuery({
    queryKey: ["creditProposal", proposalId],
    queryFn: () => {
      return getProposalData(proposalId!);
    },
    enabled: shouldFetchProposal,
    // Refetch on mount only if we don't have matching data in store
    // This ensures fresh data when store is cleared
    refetchOnMount: !hasMatchingData ? "always" : false,
    refetchOnWindowFocus: false,
    staleTime: 0 // Always consider data stale to allow refetch when store is cleared
  });

  const isReanalysisFlow =
    isReanalysisProposal(preProposalData) ||
    hasLimitReassessment(proposalData) ||
    hasLimitReassessment(queryProposalData);

  useEffect(() => {
    if (isReanalysisFlow) {
      setReanalysis(true);
    }
  }, [isReanalysisFlow, setReanalysis]);

  // Sync query data to store when query returns data but store doesn't have matching data
  // This handles the case when React Query returns cached data without calling queryFn
  useEffect(() => {
    if (
      queryProposalData &&
      proposalId &&
      String(queryProposalData.lngProposta) === String(proposalId) &&
      (!proposalData ||
        !proposalData.lngProposta ||
        String(proposalData.lngProposta) !== String(proposalId))
    ) {
      // Update store with query data if store doesn't have matching data
      const enriched = enrichCreditProposalSnapshotForPersist(
        queryProposalData,
        preProposalData
      );
      updateProposalData(mapCreditProposalToStoreData(enriched));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProposalData, proposalId]);

  // Only show loading if we're fetching and don't have matching data
  const isLoading =
    !!proposalId &&
    shouldFetchProposal &&
    (isLoadingProposal || isFetchingProposal);

  // Get personal data from proposalData
  const personalData = useMemo(() => {
    if (!proposalData) {
      return {
        birthday: "",
        gender: "",
        martialStatus: "",
        mothersName: "",
        fathersName: ""
      };
    }

    return {
      birthday: formatDateFromISO(proposalData.datDataNascimento) || "",
      gender: proposalData.strSexo || "",
      martialStatus: proposalData.strEstadoCivil || "",
      mothersName: proposalData.strNomeMae || "",
      fathersName: proposalData.strNomePai || ""
    };
  }, [proposalData]);

  const { data: martialStatusOptions } = useQuery({
    queryKey: ["martialStatus"],
    queryFn: () => commonResource.fetchMartialStatus(),
    select: (data) => {
      return data.data.map((item) => ({
        value: item.strCodEstadoCivil,
        label: item.strDescricao
      }));
    }
  });

  const { control, handleSubmit } = useForm<PersonalDataFormType>({
    resolver: zodResolver(creditProposalPersonalDataSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      birthday: personalData.birthday,
      gender: personalData.gender as GenderEnum | "" as GenderEnum,
      martialStatus: personalData.martialStatus,
      mothersName: personalData.mothersName,
      fathersName: personalData.fathersName,
      documentNumber: proposalData?.strNroDocIdent || ""
    }
  });

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (proposalData) {
      updateProposalData({
        datDataNascimento: formData.birthday
          ? new Date(
              formData.birthday.split("/").reverse().join("-")
            ).toISOString()
          : proposalData.datDataNascimento,
        strSexo:
          formData.gender && formData.gender.trim() !== ""
            ? formData.gender.trim()
            : proposalData.strSexo || "",
        strEstadoCivil: formData.martialStatus || proposalData.strEstadoCivil,
        strNomeMae: limitParentName(
          formData.mothersName || proposalData.strNomeMae || ""
        ),
        strNomePai: sanitizeParentNameForApi(formData.fathersName),
        strNroDocIdent: formData.documentNumber || proposalData.strNroDocIdent
      });
    }

    await persistFullProposalSnapshot();
    completeStep(ROUTES.creditProposalAddress);
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.proposals);
  };

  const handleClose = () => {
    resetCreditProposalStore();
    back();
  };

  return {
    control,
    handleOnSubmitForm,
    handleGoBack,
    handleClose,
    martialStatusOptions: martialStatusOptions ?? [],
    isLoadingProposal: isLoading
  };
};
